"use client";

import {
  type OpenChangeReason,
  flip,
  offset as offsetMiddleware,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { composeRefs } from "@radix-ui/react-compose-refs";
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import React, { useEffect, useId, useImperativeHandle, useState } from "react";
import type { CommonHtmlProps } from "../../../types/ui";
import { withRefProp } from "../../../utils/withRefProp";
import FloatingPanel from "../FloatingPanel/FloatingPanel";
import {
  DEFAULT_FLOATING_ALIGN_OFFSET,
  DEFAULT_FLOATING_SIDE,
  DEFAULT_FLOATING_SIDE_OFFSET,
} from "../FloatingPanel/positioning";
import { VisibilityProvider } from "../Visibility/Visibility";
import { autoUpdate } from "./AutoUpdate";
import styles from "./Popover.module.scss";
import PopoverContext, { type PopoverContextValue, usePopoverFloating } from "./PopoverContext";

export type PopoverMode = "hover" | "click" | "focus";
export type PopoverHandle = PopoverContextValue;
export const POPOVER_Z_INDEX = 9999;

export type VisibleChangeReason = OpenChangeReason;

export interface PopoverProps {
  visible?: boolean;
  onVisibleChange?: (value: boolean, event?: Event, reason?: VisibleChangeReason) => void;
  children?: React.ReactNode | ((state: PopoverContextValue) => React.ReactNode);
  disabled?: boolean;
  mode?: PopoverMode;
  fill?: boolean;
  handleRef?: React.Ref<PopoverHandle>;
  overlay?: boolean;
  offset?: number;
  animated?: boolean;
  enterDelay?: number;
  clippingRef?: React.RefObject<HTMLElement | null>;
  tags?: string[];
  interactable?: boolean;
  autoFocus?: boolean;
  placement?:
    | "top"
    | "right"
    | "bottom"
    | "left"
    | "top-start"
    | "top-end"
    | "right-start"
    | "right-end"
    | "bottom-start"
    | "bottom-end"
    | "left-start"
    | "left-end";
}

const GLOBAL_POPOVERS = new Map<string, PopoverContextValue>();

export const updatePopovers = (tag?: string) => {
  for (const popover of GLOBAL_POPOVERS.values()) {
    if (popover.visible && (!tag || popover.tags.includes(tag))) {
      popover.update();
    }
  }
};

const Popover = (props: PopoverProps) => {
  const {
    disabled,
    onVisibleChange,
    placement,
    fill,
    handleRef,
    enterDelay,
    children,
    mode = "hover",
    clippingRef,
    offset,
    tags,
    interactable = false,
    autoFocus = false,
    visible: propsVisible,
  } = props;
  const [visible, _setVisible] = React.useState(false);
  const id = useId();

  const setVisible = (nextVisible: boolean, event?: any, reason?: VisibleChangeReason) => {
    if (reason) {
      const isDismissReason = reason === "outside-press" || reason === "focus-out" || reason === "escape-key";
      const isHoverReason = reason === "hover" || reason === "focus";
      const isClickReason = reason === "click";
      const isFocusReason = reason === "focus";

      if (!isDismissReason) {
        if (mode === "hover" && !isHoverReason) return;
        if (mode === "click" && !isClickReason) return;
        if (mode === "focus" && !isFocusReason) return;
      }
    }

    _setVisible(nextVisible);
    onVisibleChange?.(nextVisible, event, reason);
  };

  const currentlyVisible = propsVisible ?? visible;

  const extraStylesRef = React.useRef<React.CSSProperties>({});

  const floating = useFloating({
    //open: true,
    open: currentlyVisible,
    onOpenChange: setVisible,
    strategy: "fixed",
    transform: false,
    placement: placement ?? DEFAULT_FLOATING_SIDE,

    // on hover

    middleware: [
      offsetMiddleware({
        mainAxis: offset ?? DEFAULT_FLOATING_SIDE_OFFSET,
        crossAxis: DEFAULT_FLOATING_ALIGN_OFFSET,
      }),
      flip({
        padding: 10,
        boundary: clippingRef?.current ?? "clippingAncestors",
        fallbackStrategy: "initialPlacement",
      }),
      shift({
        padding: 10,
        boundary: clippingRef?.current ?? "clippingAncestors",
        crossAxis: true,
      }),
      size({
        apply(state) {
          if (fill) {
            const axis = state.placement.includes("top") || state.placement.includes("bottom") ? "width" : "height";
            const newSize = state.rects.reference[axis];
            // @ts-ignore
            state.elements.floating.style[axis] = newSize;
            extraStylesRef.current[axis] = newSize;
          }
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(floating.context, {
    enabled: !disabled && mode === "hover",
    delay: {
      open: enterDelay ?? 100,
    },
    handleClose: interactable
      ? safePolygon({
          //blockPointerEvents: true,
          // tried with and without the following
          buffer: 400,
          requireIntent: false,
        })
      : undefined,
  });
  const focus = useFocus(floating.context, {
    enabled: !disabled && (mode === "hover" || mode === "focus"),
    //delay: 1000,
  });
  const click = useClick(floating.context, {
    enabled: !disabled && mode === "click",
    ignoreMouse: false,
    keyboardHandlers: mode === "click",
  });
  const dismiss = useDismiss(floating.context, {});

  const [preRendered, setPreRendered] = useState(false);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss]);
  const context = {
    ...floating.context,
    // @ts-ignore
    open: undefined,
    onOpenChange: undefined,
    setVisible: floating.context.onOpenChange,
    setVisibleChange: floating.context.onOpenChange,
    visible: currentlyVisible,
    floatingStyles: {
      ...floating.context.floatingStyles,
      ...extraStylesRef.current,
    },
    refs: {
      ...floating.context.refs,
      setFloating: (element) => {
        floating.context.refs.setFloating(element);
      },
    },
    getFloatingProps: () => ({
      ...getFloatingProps(),
      "data-popover": true,
      "data-popover-visible": floating.context.open,
      inert: !floating.context.open,
      "data-popover-placement": floating.context.placement,
    }),
    getTriggerProps: getReferenceProps,
    prerender: () => {
      setPreRendered(true);
    },
    preRendered,
    tags: tags ?? [],
    interactable,
  } satisfies PopoverContextValue;

  GLOBAL_POPOVERS.set(id, context);

  useEffect(() => {
    return () => {
      GLOBAL_POPOVERS.delete(id);
    };
  }, []);

  useImperativeHandle(handleRef, () => context);

  useEffect(() => {
    if (!currentlyVisible || !autoFocus) {
      return;
    }

    const animationFrame = requestAnimationFrame(() => {
      const floatingElement = floating.refs.floating.current;
      const focusTarget = floatingElement?.querySelector<HTMLElement>(
        [
          "input:not([disabled])",
          "textarea:not([disabled])",
          "select:not([disabled])",
          "button:not([disabled])",
          "a[href]",
          "[tabindex]:not([tabindex='-1'])",
        ].join(","),
      );

      focusTarget?.focus();
    });

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [autoFocus, currentlyVisible, floating.refs.floating]);

  return (
    <PopoverContext.Provider value={context}>
      {typeof children === "function" ? children(context) : children}
    </PopoverContext.Provider>
  );
};

export type PopoverReferenceProps<C extends React.ElementType = "button"> = React.ComponentProps<C> & {
  as?: C;
  ref?: React.Ref<C>;
  asChild?: boolean;
  trigger?: boolean;
};

const PopoverReference = withRefProp(<C extends React.ElementType = "button">(props: PopoverReferenceProps<C>) => {
  const { asChild, trigger, ref, as, ...otherProps } = props;
  const Comp: any = asChild ? Slot : (as ?? "button");
  const popoverContext = React.useContext(PopoverContext);

  const content = (
    <Comp
      {...(Comp === "button" ? { type: "button" } : {})}
      data-popover-reference={true}
      ref={composeRefs(ref, popoverContext?.refs.setReference)}
      {...otherProps}
    />
  );

  return trigger === false ? content : <PopoverTrigger asChild={true}>{content}</PopoverTrigger>;
});

export type PopoverTriggerProps<C extends React.ElementType = "button"> = React.ComponentProps<C> & {
  as?: C;
  ref?: React.Ref<React.ComponentRef<C>>;
  asChild?: boolean;
};

const PopoverTrigger = withRefProp(<C extends React.ElementType = "button">(props: PopoverTriggerProps<C>) => {
  const { asChild, ref, as, ...otherProps } = props;
  const Comp: any = asChild ? Slot : (as ?? "button");
  const popoverContext = React.useContext(PopoverContext);

  return (
    <Comp
      data-popover-trigger={true}
      ref={composeRefs(ref)}
      onMouseOver={(e: React.MouseEvent<HTMLElement>) => {
        popoverContext?.prerender();
        otherProps?.onMouseOver?.(e);
      }}
      onMouseDown={(e: React.MouseEvent<HTMLElement>) => {
        popoverContext?.prerender();
        otherProps?.onMouseDown?.(e);
      }}
      {...popoverContext?.getTriggerProps?.()}
      {...otherProps}
    />
  );
});

export type PopoverContentProps = CommonHtmlProps & {
  asChild?: boolean;
  handleVisibility?: boolean;
  portal?: boolean;
};

const PopoverContent = withRefProp((props: PopoverContentProps) => {
  const { children, ref, handleVisibility, asChild, className, portal, style, ...otherProps } = props;

  const floating = usePopoverFloating({ portal });
  if (!floating) {
    return null;
  }

  const { ref: floatingRef, ...floatingProps } = floating.props;

  const Tag = asChild ? Slot : "div";

  return floating.render(
    <VisibilityProvider value={{ visible: floating.context.visible }}>
      <Tag
        {...floatingProps}
        // @ts-ignore
        ref={composeRefs(floatingRef, ref)}
        style={{ ...floating.props.style, zIndex: POPOVER_Z_INDEX, ...style }}
        data-interactable={floating.context.interactable}
        data-visible={floating.context.visible}
        data-handle-visiblity={handleVisibility !== false}
        className={clsx(styles.content, className)}
        {...otherProps}
      >
        {children}
      </Tag>
    </VisibilityProvider>,
  );
});

export default Object.assign(Popover, {
  Reference: PopoverReference,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
});
